import type { PaymentDiscountShape } from '@algoacademy/backend/modules/payment/enums'
import type { MentoringDiscountInput } from '../../../_components/DiscountControls'

/**
 * Only the fields formatDiscountSummary reads, so a test can build one without a
 * whole DTO. Structurally the same as `mentoringState.unpaidOffer.discount`, so
 * whatever the query returns can be passed straight in.
 */
type UnpaidOfferDiscountLike = {
  shape: PaymentDiscountShape
  shapeLabel: string
  value: number
  periodCount: number | null
  remainingPeriods: number | null
  reason: string | null
}

type DroppedDiscountArgs = {
  /** The discount this submission is about to send - null means "list price on purpose". */
  submittedDiscount: MentoringDiscountInput | null
  /** The discount on the student's unpaid offer, as the mentoring state query holds it. */
  unpaidOfferDiscount: UnpaidOfferDiscountLike | null | undefined
}

/**
 * Which discount, if any, this submission is about to wipe.
 *
 * An offer that states "list price" writes `null` straight over the stored
 * discount columns - it does not merge with whatever was there before. So a
 * student who already has a discount on their still-unpaid offer loses it the
 * moment staff submits full price, and nothing on screen remembers it well
 * enough to retype it back afterwards: the offer is the only place it lived.
 *
 * Returns null whenever nothing would be lost - the submission still states a
 * discount, or the student had none on their unpaid offer to begin with.
 */
export function readDroppedDiscount({
  submittedDiscount,
  unpaidOfferDiscount
}: DroppedDiscountArgs): UnpaidOfferDiscountLike | null {
  if (submittedDiscount !== null) return null
  if (unpaidOfferDiscount === null || unpaidOfferDiscount === undefined) return null

  return unpaidOfferDiscount
}
